import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Table, Button, Container, Pagination } from 'react-bootstrap';
import { FileText, Trash2, Plus, Eye, AlertCircle, RefreshCw, Pencil } from 'lucide-react';
import { getManagement, deleteManagement, storeManagement, updateManagementMember } from '../../Services/ManagementService';
import { openFile } from '../../utils/helper';
import { format } from 'date-fns';
import usePagination from '../../hooks/usePagination';
import ModalForm from "../../Components/ModalForm";
import Swal from 'sweetalert2'
import Loader from '../../Components/Loader';
import { Helmet } from "react-helmet-async";

const ManageExecutives= () => {
    const [showModal, setShowModal] = useState(false);
    const [id, setId] = useState('');
    const [validationerror, setValidationError] = useState('');
    const {
        currentPage,
        totalPages,
        data: managementDetails,
        loading,
        error,
        handlePageChange,
        fetchPageData,
    } = usePagination(getManagement);

    useEffect(() => {
        if (managementDetails.length === 0) {
            fetchPageData();
        }
    }, [managementDetails]);

    const handleRefresh = () => {
        fetchPageData();
    };

    const handleDelete = async (id) => {
        await deleteManagement(id);
        fetchPageData();
    };


    const validateFormData = (formData) => {
        const validateImage = () => {
            const MAX_FILE_SIZE = 5 * 1024 * 1024;
            const validImageTypes = ['image/png', 'image/jpeg', 'image/jpg'];
            const { file } = formData;

            if (!file && id) {
                return true;
            }

            if (!file) {
                setValidationError('Please upload a profile picture');
                return false;
            }

            if (file && file.size > MAX_FILE_SIZE) {
                setValidationError('File size exceeds limit (5MB),');
                return false;
            }
            if (file && !validImageTypes.includes(file.type)) {
                setValidationError('Invalid file type. Only PNG, JPEG, or JPG files are allowed for the Management members.');
                return false;
            }
            return true;
        }

        if (!id) {
            return validateImage();
        }

        if (id) {
            if (id && Object.values(formData).every(value => !value)) {
                setValidationError('You must fill out at least one field to update.');
                return false;
            }

            if (formData?.file) {
                return validateImage();
            }
            return true;
        }
        return true;
    };

    const handleOpenModal = () => setShowModal(true);
    const handleCloseModal = () => {
        setShowModal(false);
        setId('');
    }
    const handleFormSubmit = async (formData) => {
        if (validateFormData(formData)) {
            const formDataToSubmit = new FormData();
            if (formData?.name) formDataToSubmit.append('name', formData?.name);
            if (formData?.position) formDataToSubmit.append('position', formData?.position);
            if (formData?.institute) formDataToSubmit.append('institute', formData?.institute);
            if (formData?.wing) formDataToSubmit.append('wing', formData?.wing);
            if (formData?.file) formDataToSubmit.append('file', formData?.file);

            !id
                ? await storeManagement(formDataToSubmit)
                : await updateManagementMember(id, formDataToSubmit)

            fetchPageData();
        }
        else {
            Swal.fire({ title: `${validationerror} Please try again.`, icon: "error" });
        }
    }

    if (loading) {
        return (
            <Container className="d-flex justify-content-center align-items-center p-5">
                <Loader />
            </Container>
        );
    }

    if (error) {
        return (
            <Container className="p-4 text-danger">
                <AlertCircle className="me-2" />
                {error}
            </Container>
        );
    }

    return (
        <>
            <Helmet>
                <title>Manage Executives</title>
                <meta name="description" content="Oasis Educational Institute" />
            </Helmet>

            <Container className="py-5">
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h2 className="mb-0 fw-5">
                        <FileText className="me-2" />
                        <strong>Manage Executives</strong>
                    </h2>
                    <div>
                        <Button
                            variant="outline-primary"
                            className="me-2"
                            onClick={handleRefresh}
                            disabled={loading}
                        >
                            <RefreshCw className="me-1" size={12} />
                            {loading ? 'Refreshing...' : 'Refresh'}
                        </Button>
                        <Button variant="primary" onClick={handleOpenModal} >
                            <Plus className="me-1" size={12} />
                            Add New
                        </Button>
                    </div>
                </div>
                {managementDetails.length === 0 ? (
                    <div className=" d-flex justify-content-center align-content-center text-center p-4 bg-light rounded">
                        <FileText size={48} className="text-muted mb-3" />
                        <p className="font-bold mb-2">No record found for Management</p>
                    </div>
                ) : (
                    <Table hover responsive className="align-middle">
                        <thead className="bg-light">
                            <tr>
                                <th>#</th>
                                <th>Name</th>
                                <th>Position</th>
                                <th>Institute</th>
                                <th>Wing</th>
                                <th>Uploaded At</th>

                                <th className="text-center">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {managementDetails.map((management, index) => (
                                <tr key={index}>
                                    <td>{index + 1}</td>
                                    <td> <Link onClick={() => openFile(management.file_id)}> {management.name} </Link></td>
                                    <td>{management.position}</td>
                                    <td> {management.institute}</td>
                                    <td>{management.wing}</td>
                                    <td>
                                        {management.updated_at ? format(new Date(management.updated_at), 'MMMM dd, yyyy') : 'Unknown'}
                                    </td>
                                    <td>
                                        <div className="d-flex justify-content-center gap-2">
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                onClick={() => openFile(management.file_id)}
                                            >
                                                <Eye size={16} />
                                            </Button>
                                            <Button
                                                variant="outline-secondary"
                                                className="text-gray-600"
                                                size="sm"
                                                onClick={() => {
                                                    setId(management.id)
                                                    handleOpenModal()
                                                }}
                                            >
                                                <Pencil size={16} />
                                            </Button>
                                            <Button
                                                variant="outline-danger"
                                                size="sm"
                                                onClick={() => handleDelete(management.id)}
                                            >
                                                <Trash2 size={16} />
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                )}
                {managementDetails.length !== 0 &&
                    (<Pagination>
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNumber) => (
                            <Pagination.Item
                                key={pageNumber}
                                active={pageNumber === currentPage}
                                onClick={() => handlePageChange(pageNumber)}
                            >
                                {pageNumber}
                            </Pagination.Item>
                        ))}
                    </Pagination>
                    )}

            </Container>
            <ModalForm
                show={showModal}
                onClose={handleCloseModal}
                module="management"
                id= {id?? id}
                title={!id ? "Add new management member" : 'Edit Management member'}
                description="Please ensure that the picture is in JPG, JPEG, or PNG format and does not exceed 5MB in size."
                fields={[
                    { label: "Name", name: "name", type: "text", required: id ? false : true, placeholder: "Enter the name of the management member" },
                    { label: "Position", name: "position", type: "text", required: id ? false : true, placeholder: "Enter the position of a management member", },
                    { label: "Institute", name: "institute", type: "text", required: id ? false : true, placeholder: "Name of the institute management member works at", },
                    { label: "Wing", name: "wing", type: "text", required: id ? false : true, placeholder: "Enter the branch of the institute", },
                    { label: "Profile Picture", name: "file", type: "file", required: id ? false : true, },

                ]}
                onSubmit={handleFormSubmit}
                buttonText={!id ? "Add" : "Update"}
            />
        </>

    );
};

export default ManageExecutives;